// src/features/videos/meta.ts — static tables + display helpers for the Videos page.
//
// Chip a11y model note: web (VideoChips.tsx) uses a `role="group"` container of
// plain buttons with `aria-pressed` — a filter-toggle idiom, not a tab panel
// (there's no single "tabpanel" being switched, just a filtered list). Mobile
// (VideosScreen.tsx) uses `accessibilityRole="tablist"`/`"tab"`, matching the
// existing CreatorsHubScreen chip-row convention on that platform. This is a
// deliberate per-platform choice, not drift — keep it that way rather than
// "fixing" one to match the other.
import type { VideoChipId } from '@/core/models/video';

export interface ChipDef {
  id: VideoChipId;
  label: string;
}

/** The web chip row (full set). */
export const WEB_CHIPS: ChipDef[] = [
  { id: 'for-you', label: 'For you' },
  { id: 'trending', label: 'Trending' },
  { id: 'latest', label: 'Latest' },
  { id: 'following', label: 'Following' },
  { id: 'tech', label: 'Tech' },
  { id: 'science', label: 'Science' },
  { id: 'news', label: 'News' },
  { id: 'gaming', label: 'Gaming' },
  { id: 'education', label: 'Education' },
  { id: 'music', label: 'Music' },
];

/** Mobile shows deliberately fewer chips than web (owner decision). */
export const MOBILE_CHIPS: ChipDef[] = WEB_CHIPS.slice(0, 4);

/** mm:ss (or h:mm:ss past an hour) display for a seed duration in seconds. */
export function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Compact view-count display, e.g. 128000 -> "128K". */
export function formatViews(n: number): string {
  if (n < 1000) return `${n} views`;
  if (n < 1_000_000) return `${trim(n / 1000)}K views`;
  return `${trim(n / 1_000_000)}M views`;
}

function trim(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '');
}

/**
 * Shown on the SAMPLE view only (2026-09-26): until any member posts a real video, the
 * page keeps its seeded samples, each wearing a "Sample" badge (owner decision), and this
 * line says so plainly. Once a real video exists the page switches to real data
 * (real/RealVideosPage.tsx) and neither the samples nor this notice appear.
 */
export const PREVIEW_NOTICE =
  'Sample videos: nothing here is real yet. They show how this page will look; real videos appear here as soon as members post them.';
